import { z } from "zod";
import { OffsetPageQuerySchema } from "@/shared/contracts/base";

export const IMPORT_ORDER_STATUSES = [
  "draft",
  "ordered",
  "at_carrier",
  "partially_received",
  "received",
  "cancelled",
] as const;

export const IMPORT_ORDER_SOURCES = [
  "taobao",
  "1688",
  "pinduoduo",
  "other",
] as const;

export const IMPORT_ORDER_CURRENCIES = ["CNY", "LAK"] as const;

export const IMPORT_ORDER_TRACKING_STATUSES = [
  "in_transit",
  "arrived",
  "delivered",
  "unknown",
] as const;

export const ImportOrderStatusSchema = z.enum(IMPORT_ORDER_STATUSES);
export type ImportOrderStatusDTO = z.infer<typeof ImportOrderStatusSchema>;

export const ImportOrderSourceSchema = z.enum(IMPORT_ORDER_SOURCES);
export type ImportOrderSourceDTO = z.infer<typeof ImportOrderSourceSchema>;

export const ImportOrderTrackingStatusSchema = z.enum(
  IMPORT_ORDER_TRACKING_STATUSES,
);
export type ImportOrderTrackingStatusDTO = z.infer<
  typeof ImportOrderTrackingStatusSchema
>;

export const IMPORT_ORDER_LINE_MATCH_STATUSES = [
  "pending",
  "matched",
  "skipped",
] as const;

export const ImportOrderLineMatchStatusSchema = z.enum(
  IMPORT_ORDER_LINE_MATCH_STATUSES,
);
export type ImportOrderLineMatchStatusDTO = z.infer<
  typeof ImportOrderLineMatchStatusSchema
>;

/** รายการ matched — มี variant ในระบบ */
export const ImportOrderMatchedLineInputSchema = z.object({
  kind: z.literal("matched").optional().default("matched"),
  variantId: z.string().min(1),
  description: z.string().max(500).optional().nullable(),
  orderedQuantity: z.coerce.number().int().min(1),
  unitPriceForeign: z.coerce.number().min(0),
});

/** รายการ pending — ยังไม่มี SKU */
export const ImportOrderPendingLineInputSchema = z.object({
  kind: z.literal("pending"),
  externalTitle: z.string().min(1).max(500),
  externalUrl: z.string().max(2000).optional().nullable(),
  externalImageKey: z.string().max(500).optional().nullable(),
  description: z.string().max(500).optional().nullable(),
  orderedQuantity: z.coerce.number().int().min(1),
  unitPriceForeign: z.coerce.number().min(0),
});

function normalizeImportOrderLineInput(value: unknown): unknown {
  if (!value || typeof value !== "object") return value;
  const row = value as Record<string, unknown>;
  if (row.kind === "pending") return value;
  if (row.kind === "matched") return value;
  if (typeof row.variantId === "string" && row.variantId.length > 0) {
    return { ...row, kind: "matched" };
  }
  return value;
}

/** รายการสินค้า — input ตอนสร้าง/แก้ใบ (matched | pending) */
export const ImportOrderLineInputSchema = z.preprocess(
  normalizeImportOrderLineInput,
  z.discriminatedUnion("kind", [
    ImportOrderMatchedLineInputSchema.extend({ kind: z.literal("matched") }),
    ImportOrderPendingLineInputSchema,
  ]),
);
export type ImportOrderLineInputDTO = z.infer<
  typeof ImportOrderLineInputSchema
>;
export type ImportOrderMatchedLineInputDTO = z.infer<
  typeof ImportOrderMatchedLineInputSchema
>;
export type ImportOrderPendingLineInputDTO = z.infer<
  typeof ImportOrderPendingLineInputSchema
>;

/** รายการสินค้า — รูปแบบเต็ม (response / ภายใน domain) */
export const ImportOrderLineSchema = z.object({
  id: z.string().min(1),
  importOrderId: z.string().min(1),
  matchStatus: ImportOrderLineMatchStatusSchema,
  variantId: z.string().nullable(),
  externalTitle: z.string().nullable().optional(),
  externalUrl: z.string().nullable().optional(),
  externalImageKey: z.string().nullable().optional(),
  matchedAt: z.coerce.date().nullable().optional(),
  matchedBy: z.string().nullable().optional(),
  skippedAt: z.coerce.date().nullable().optional(),
  skipNote: z.string().nullable().optional(),
  description: z.string().nullable().optional(),
  orderedQuantity: z.number().int().min(1),
  receivedQuantity: z.number().int().min(0),
  shortQuantity: z.number().int().min(0),
  unitPriceForeign: z.string(),
  goodsCostForeign: z.string(),
  goodsCostLak: z.string(),
  allocatedShippingLak: z.string(),
  landedCostLak: z.string(),
  unitLandedCostLak: z.string(),
  productName: z.string(),
  variantName: z.string(),
  stockUnitLabel: z.string().optional().default("ຊິ້ນ"),
  receiveUnitLabel: z.string().nullable().optional().default(null),
  receiveConversionFactor: z.number().positive().optional().default(1),
  remainingQuantity: z.number().int().min(0),
  createdAt: z.coerce.date(),
  updatedAt: z.coerce.date(),
});
export type ImportOrderLineDetailDTO = z.infer<typeof ImportOrderLineSchema>;

export const CreateProductFromImportLineSchema = z.object({
  categoryIds: z.array(z.string().min(1)).min(1),
  productName: z.string().min(1).max(200),
  variantName: z.string().min(1).max(120).optional().default("ມາດຕະຐານ"),
  stockUnitLabel: z.string().max(32).optional(),
  receiveUnitLabel: z.string().max(32).optional().nullable(),
  receiveConversionFactor: z.coerce.number().positive().optional(),
  price: z.coerce.number().int().min(0).optional().default(0),
  sku: z.string().max(64).optional().nullable(),
});
export type CreateProductFromImportLineDTO = z.infer<
  typeof CreateProductFromImportLineSchema
>;

export const CreateVariantFromImportLineSchema = z.object({
  productId: z.string().min(1),
  variantName: z.string().min(1).max(120).optional().default("ມາດຕະຐານ"),
  stockUnitLabel: z.string().max(32).optional(),
  receiveUnitLabel: z.string().max(32).optional().nullable(),
  receiveConversionFactor: z.coerce.number().positive().optional(),
  price: z.coerce.number().int().min(0).optional().default(0),
  sku: z.string().max(64).optional().nullable(),
});
export type CreateVariantFromImportLineDTO = z.infer<
  typeof CreateVariantFromImportLineSchema
>;

export const MatchImportOrderLineSchema = z.union([
  z.object({ variantId: z.string().min(1) }),
  z.object({ createProduct: CreateProductFromImportLineSchema }),
  z.object({ createVariant: CreateVariantFromImportLineSchema }),
]);
export type MatchImportOrderLineDTO = z.infer<
  typeof MatchImportOrderLineSchema
>;

export const SkipImportOrderLineSchema = z.object({
  note: z.string().max(500).optional().nullable(),
});
export type SkipImportOrderLineDTO = z.infer<typeof SkipImportOrderLineSchema>;

export const ImportOrderTrackingSchema = z.object({
  id: z.string().min(1),
  importOrderId: z.string().min(1),
  trackingNo: z.string().min(1).max(120),
  carrierName: z.string().max(120).nullable().optional(),
  status: ImportOrderTrackingStatusSchema,
  shippedAt: z.coerce.date().nullable().optional(),
  arrivedAt: z.coerce.date().nullable().optional(),
  note: z.string().max(500).nullable().optional(),
  createdAt: z.coerce.date(),
  updatedAt: z.coerce.date(),
});
export type ImportOrderTrackingDTO = z.infer<typeof ImportOrderTrackingSchema>;

export const ImportOrderTrackingInputSchema = z.object({
  trackingNo: z.string().min(1).max(120),
  carrierName: z.string().max(120).optional().nullable(),
  status: ImportOrderTrackingStatusSchema.optional().default("in_transit"),
  shippedAt: z.string().optional().nullable(),
  arrivedAt: z.string().optional().nullable(),
  note: z.string().max(500).optional().nullable(),
});
export type ImportOrderTrackingInputDTO = z.infer<
  typeof ImportOrderTrackingInputSchema
>;

export const UpdateImportOrderTrackingSchema =
  ImportOrderTrackingInputSchema.partial();
export type UpdateImportOrderTrackingDTO = z.infer<
  typeof UpdateImportOrderTrackingSchema
>;

export const CreateImportOrderSchema = z.object({
  externalOrderNo: z.string().min(1).max(200),
  source: ImportOrderSourceSchema,
  currency: z.enum(IMPORT_ORDER_CURRENCIES).optional().default("LAK"),
  exchangeRate: z.coerce.number().positive(),
  shippingTotalLak: z.coerce.number().min(0).optional().default(0),
  orderedAt: z.string().optional().nullable(),
  expectedArrivalDate: z.string().optional().nullable(),
  note: z.string().max(1000).optional().nullable(),
  status: z.enum(["draft", "ordered"]).optional().default("draft"),
  lines: z.array(ImportOrderLineInputSchema).min(1),
  trackings: z.array(ImportOrderTrackingInputSchema).optional().default([]),
});
export type CreateImportOrderDTO = z.infer<typeof CreateImportOrderSchema>;

export const UpdateImportOrderLinePriceSchema = z.object({
  importOrderLineId: z.string().min(1),
  unitPriceForeign: z.coerce.number().min(0),
});
export type UpdateImportOrderLinePriceDTO = z.infer<
  typeof UpdateImportOrderLinePriceSchema
>;

export const UpdateImportOrderLineQuantitySchema = z.object({
  importOrderLineId: z.string().min(1),
  orderedQuantity: z.coerce.number().int().min(1),
});
export type UpdateImportOrderLineQuantityDTO = z.infer<
  typeof UpdateImportOrderLineQuantitySchema
>;

export const UpdateImportOrderSchema = z
  .object({
    externalOrderNo: z.string().min(1).max(200).optional(),
    source: ImportOrderSourceSchema.optional(),
    currency: z.enum(IMPORT_ORDER_CURRENCIES).optional(),
    exchangeRate: z.coerce.number().positive().optional(),
    shippingTotalLak: z.coerce.number().min(0).optional(),
    orderedAt: z.string().optional().nullable(),
    expectedArrivalDate: z.string().optional().nullable(),
    note: z.string().max(1000).optional().nullable(),
    lines: z.array(ImportOrderLineInputSchema).min(1).optional(),
    linePrices: z.array(UpdateImportOrderLinePriceSchema).min(1).optional(),
    lineQuantities: z
      .array(UpdateImportOrderLineQuantitySchema)
      .min(1)
      .optional(),
    trackings: z.array(ImportOrderTrackingInputSchema).optional(),
  })
  .refine((value) => Object.keys(value).length > 0, {
    message: "ຕ້ອງມີຟິວດຢ່າງໜ້ອຍ 1 ຟິວດ",
  });
export type UpdateImportOrderDTO = z.infer<typeof UpdateImportOrderSchema>;

export const MarkImportOrderAtCarrierSchema = z.object({
  exchangeRate: z.coerce.number().positive(),
  shippingTotalLak: z.coerce.number().min(0),
  linePrices: z.array(UpdateImportOrderLinePriceSchema).min(1),
  trackings: z.array(ImportOrderTrackingInputSchema).optional().default([]),
});
export type MarkImportOrderAtCarrierDTO = z.infer<
  typeof MarkImportOrderAtCarrierSchema
>;

export const ReceiveImportOrderLineSchema = z.object({
  importOrderLineId: z.string().min(1),
  receivedQuantity: z.coerce.number().int().min(1),
});

export const ReceiveImportOrderSchema = z.object({
  receivedAt: z.string().min(1),
  note: z.string().max(1000).optional().nullable(),
  exchangeRate: z.coerce.number().positive(),
  shippingTotalLak: z.coerce.number().min(0),
  linePrices: z.array(UpdateImportOrderLinePriceSchema).min(1),
  trackingNo: z.string().max(120).optional().nullable(),
  carrierName: z.string().max(200).optional().nullable(),
  lines: z.array(ReceiveImportOrderLineSchema).min(1),
});
export type ReceiveImportOrderDTO = z.infer<typeof ReceiveImportOrderSchema>;

export const MarkImportOrderShortLineSchema = z.object({
  importOrderLineId: z.string().min(1),
  shortQuantity: z.coerce.number().int().min(1),
});

export const MarkImportOrderShortSchema = z.object({
  note: z.string().max(1000).optional().nullable(),
  lines: z.array(MarkImportOrderShortLineSchema).min(1),
});
export type MarkImportOrderShortDTO = z.infer<
  typeof MarkImportOrderShortSchema
>;

/** Body ສຳລັບ preview landed cost — ຄຳນວນຈາກ payload ໂດຍກົງ (ບໍ່ຕ້ອງມີ order id) */
export const LandedCostPreviewSchema = z.object({
  exchangeRate: z.coerce.number().positive(),
  currency: z.enum(IMPORT_ORDER_CURRENCIES).optional().default("LAK"),
  shippingTotalLak: z.coerce.number().min(0).optional().default(0),
  lines: z
    .array(
      z.object({
        variantId: z.string().min(1).optional(),
        productName: z.string().optional(),
        variantName: z.string().optional(),
        description: z.string().max(500).optional().nullable(),
        orderedQuantity: z.coerce.number().int().min(1),
        unitPriceForeign: z.coerce.number().min(0),
      }),
    )
    .min(1),
});
export type LandedCostPreviewInputDTO = z.infer<typeof LandedCostPreviewSchema>;

export const ImportOrderListQuerySchema = OffsetPageQuerySchema.extend({
  status: ImportOrderStatusSchema.optional(),
  source: ImportOrderSourceSchema.optional(),
  search: z.string().optional(),
  overdueOnly: z.coerce.boolean().optional().default(false),
});
export type ImportOrderListQueryDTO = z.infer<
  typeof ImportOrderListQuerySchema
>;

function normalizeVariantIdsQuery(value: unknown): string[] {
  if (Array.isArray(value)) {
    return value.flatMap((entry) =>
      typeof entry === "string"
        ? entry
            .split(",")
            .map((id) => id.trim())
            .filter(Boolean)
        : [],
    );
  }
  if (typeof value === "string" && value.trim()) {
    return value
      .split(",")
      .map((id) => id.trim())
      .filter(Boolean);
  }
  return [];
}

export const VariantIncomingQuerySchema = z.object({
  variantIds: z.preprocess(
    normalizeVariantIdsQuery,
    z.array(z.string().min(1)).min(1),
  ),
});

export const VariantLastPricesQuerySchema = z.object({
  variantIds: z.preprocess(
    normalizeVariantIdsQuery,
    z.array(z.string().min(1)).min(1),
  ),
});

export const UpdateImportExchangeSettingsSchema = z.object({
  defaultExchangeRate: z.coerce.number().positive(),
});

export const InTransitReportQuerySchema = z.object({
  source: ImportOrderSourceSchema.optional(),
  status: ImportOrderStatusSchema.optional(),
  overdueOnly: z.coerce.boolean().optional().default(false),
});
export type InTransitReportQueryDTO = z.infer<
  typeof InTransitReportQuerySchema
>;

export const PendingImportLinesQuerySchema = z.object({
  limit: z.coerce.number().int().min(1).max(100).optional().default(50),
});
export type PendingImportLinesQueryDTO = z.infer<
  typeof PendingImportLinesQuerySchema
>;

export const ImportOrderIdParamSchema = z.object({
  id: z.string().min(1),
});

export const ImportOrderLineIdParamSchema = z.object({
  id: z.string().min(1),
  lineId: z.string().min(1),
});

export const VariantIdParamSchema = z.object({
  variantId: z.string().min(1),
});

export const ImportOrderTrackingIdParamSchema = z.object({
  id: z.string().min(1),
  trackingId: z.string().min(1),
});

export const ImportOrderReceiptIdParamSchema = z.object({
  id: z.string().min(1),
  receiptId: z.string().min(1),
});

export const ImportOrderGoodsReceiptIdParamSchema = z.object({
  id: z.string().min(1),
  goodsReceiptId: z.string().min(1),
});
